import { useState } from "react";
import {
	Box,
	Button,
	Divider,
	FormLabel,
	FormControl,
	Link,
	TextField,
	Typography,
	Stack,
	Select,
	MenuItem,
	InputLabel,
	FormHelperText,
} from "@mui/material";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { Navigate } from "react-router-dom";
import { useCurrenciesQuery } from "../../services/queryHooks";
import { registerUser } from "../../services/api";
import { Loading } from "../../components/common/Loading";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import CheckIcon from "@mui/icons-material/Check";
import { validateEmail, validateTextField } from "../../utils/validation";

const Card = styled(MuiCard)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignSelf: "center",
	width: "100%",
	maxHeight: "90vh",
	padding: theme.spacing(4),
	gap: theme.spacing(2),
	margin: "auto",
	overflowY: "auto",
	boxShadow:
		"hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
	[theme.breakpoints.up("sm")]: {
		width: "450px",
	},
	...theme.applyStyles("dark", {
		boxShadow:
			"hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
	}),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
	height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
	minHeight: "100%",
	padding: theme.spacing(2),
	[theme.breakpoints.up("sm")]: {
		padding: theme.spacing(4),
	},
	"&::before": {
		content: '""',
		display: "block",
		position: "absolute",
		zIndex: -1,
		inset: 0,
		backgroundImage:
			"radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
		backgroundRepeat: "no-repeat",
		...theme.applyStyles("dark", {
			backgroundImage:
				"radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
		}),
	},
}));

export default function Register() {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [name, setName] = useState("");
	const [surname, setSurname] = useState("");
	const [currency, setCurrency] = useState("");

	const [emailErrorMessage, setEmailErrorMessage] = useState("");
	const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
	const [nameErrorMessage, setNameErrorMessage] = useState("");
	const [surnameErrorMessage, setSurnameErrorMessage] = useState("");
	const [currencyErrorMessage, setCurrencyErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	const isAuthenticated = localStorage.getItem("authToken");
	const { data: currencies, isPending } = useCurrenciesQuery();
	const [isLoading, setIsLoading] = useState(false);

	const { t } = useTranslation();

	const handleCurrency = (event) => {
		setCurrency(event.target.value);
	};

	const validateInputs = () => {
		let isValid = true;

		const emailError = validateEmail(email, t);
		setEmailErrorMessage(emailError);
		if (emailError) isValid = false;

		if (!password.trim() || password.length < 6) {
			setPasswordErrorMessage(t("passwordMustBeAtLeast6Characters"));
			isValid = false;
		} else {
			setPasswordErrorMessage("");
		}

		const nameError = validateTextField(name, t, {
			fieldName: t("nameOfUser"),
		});
		setNameErrorMessage(nameError);
		if (nameError) isValid = false;

		const surnameError = validateTextField(surname, t, {
			fieldName: t("surname"),
		});
		setSurnameErrorMessage(surnameError);
		if (surnameError) isValid = false;

		if (!currency) {
			setCurrencyErrorMessage(t("pleaseChooseUserCurrency"));
			isValid = false;
		} else {
			setCurrencyErrorMessage("");
		}

		return isValid;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (
			nameErrorMessage !== "" ||
			surnameErrorMessage !== "" ||
			emailErrorMessage !== "" ||
			passwordErrorMessage !== "" ||
			currencyErrorMessage !== ""
		) {
			return;
		}
		setIsLoading(true);
		try {
			await registerUser({ name, surname, email, password, currency });
			setSuccessMessage(t("accountRegisteredSuccessfully"));
			setTimeout(() => {
				window.location.href = "/login";
			}, 4000);
		} catch (err) {
			console.error(err?.message || "Register failed. Please try again.");
			setIsLoading(false);
		}
	};

	if (!isAuthenticated) {
		return (
			<>
				{successMessage && (
					<Box
						sx={{
							position: "fixed",
							top: "100px",
							left: "50%",
							transform: "translateX(-50%)",
							zIndex: 5,
							width: "auto",
						}}
					>
						<Alert
							icon={<CheckIcon fontSize="inherit" />}
							severity="success"
						>
							{successMessage}
						</Alert>
					</Box>
				)}
				<SignUpContainer
					direction="column"
					justifyContent="space-between"
				>
					<Card variant="outlined">
						{(isPending || isLoading) && <Loading />}
						<Typography
							component="h1"
							variant="h4"
							sx={{
								width: "100%",
								fontSize: "clamp(2rem, 10vw, 2.15rem)",
							}}
						>
							{t("signUp")}
						</Typography>
						<Box
							component="form"
							onSubmit={handleSubmit}
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1,
							}}
						>
							<FormControl>
								<FormLabel htmlFor="name">
									{t("nameOfUser")}
								</FormLabel>
								<TextField
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="Jon"
									error={nameErrorMessage !== ""}
									helperText={nameErrorMessage}
								/>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="surname">
									{t("surname")}
								</FormLabel>
								<TextField
									type="text"
									value={surname}
									onChange={(e) => setSurname(e.target.value)}
									placeholder="Snow"
									error={surnameErrorMessage !== ""}
									helperText={surnameErrorMessage}
								/>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="email">
									{t("email")}
								</FormLabel>
								<TextField
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="your@email.com"
									error={emailErrorMessage !== ""}
									helperText={emailErrorMessage}
								/>
							</FormControl>
							<FormControl>
								<FormLabel htmlFor="password">
									{t("password")}
								</FormLabel>
								<TextField
									placeholder="••••••"
									type="password"
									value={password}
									onChange={(e) =>
										setPassword(e.target.value)
									}
									error={passwordErrorMessage !== ""}
									helperText={passwordErrorMessage}
								/>
							</FormControl>
							<FormControl error={currencyErrorMessage !== ""}>
								<InputLabel id="demo-simple-select-label">
									{t("currency")}
								</InputLabel>
								<Select
									label={t("currency")}
									error={currencyErrorMessage !== ""}
									value={currency}
									onChange={handleCurrency}
								>
									{currencies?.map((currency) => (
										<MenuItem
											key={currency}
											value={currency}
										>
											{currency}
										</MenuItem>
									))}
								</Select>
								{currencyErrorMessage && (
									<FormHelperText>
										{currencyErrorMessage}
									</FormHelperText>
								)}
							</FormControl>
							<Button
								type="submit"
								fullWidth
								variant="contained"
								onClick={validateInputs}
								sx={{ mt: 1 }}
							>
								{t("signUp")}
							</Button>
						</Box>
						<Divider></Divider>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 2,
							}}
						>
							<Typography sx={{ textAlign: "center" }}>
								{t("alreadyHaveAnAccount")}{" "}
								<Link
									href="/login"
									variant="body2"
									sx={{ alignSelf: "center" }}
								>
									{t("signIn")}
								</Link>
							</Typography>
						</Box>
					</Card>
				</SignUpContainer>
			</>
		);
	} else {
		return <Navigate to="/" />;
	}
}
